import React from 'react';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { GripVertical, Clock, FileAudio } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PlanElement } from '@/services/api';
import { cn } from '@/lib/utils';
import { formatDurationShort, formatTime } from '@/lib/timeCalculations';
import { SortableOverskriftRow } from './SortableOverskriftRow';
import { isPlanRadSortable } from '@/lib/planDndUtils';

/**
 * Plan element list with drag-and-drop support
 * 
 * Displays:
 * - Empty state when no overskrifter exist
 * - List of sortable overskrifter with nested hendelser
 * - Drag overlay for visual feedback
 * 
 * Extracted from PlanEditor.tsx for better modularity.
 */

interface PlanElementListProps {
  elements: PlanElement[];
  overskrifter: PlanElement[];
  expandedHeaders: Set<number>;
  activeElement: PlanElement | null;
  sensors: any;
  onDragStart: (event: any) => void;
  onDragOver: (event: any) => void;
  onDragEnd: (event: any) => void;
  onDragCancel: () => void;
  onToggleHeader: (id: number) => void;
  onEdit: (element: PlanElement) => void;
  onDelete: (id: number) => void;
  onOpenHendelseDialog: (parentId: number) => void;
  onOpenSangDialog: (parentId: number) => void;
  onSelectSong?: (placeholder: PlanElement) => void;
  calculateStartTime: (id: number) => Date | null;
  getHendelserForOverskrift: (overskriftId: number) => PlanElement[];
  readOnly?: boolean;
}

export const PlanElementList: React.FC<PlanElementListProps> = ({
  elements,
  overskrifter,
  expandedHeaders,
  activeElement,
  sensors,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDragCancel,
  onToggleHeader,
  onEdit,
  onDelete,
  onOpenHendelseDialog,
  onOpenSangDialog,
  onSelectSong,
  calculateStartTime,
  getHendelserForOverskrift,
  readOnly = false,
}) => {
  if (overskrifter.length === 0) {
    return (
      <div className="bg-muted rounded-xl p-8 sm:p-12 text-center border-2 border-dashed">
        <p className="text-sm sm:text-base text-muted-foreground">
          {readOnly 
            ? 'Ingen innslag ennå.' 
            : 'Ingen innslag ennå. Legg til en overskrift for å komme i gang.'}
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext
        items={[
          ...overskrifter.map(o => o.id),
          ...elements.filter(isPlanRadSortable).map(h => h.id),
        ]}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2 sm:space-y-3">
          {overskrifter.map((overskrift) => (
            <div key={overskrift.id} className="bg-card rounded-lg shadow-md border overflow-hidden">
              <SortableOverskriftRow 
                overskrift={overskrift}
                hendelser={getHendelserForOverskrift(overskrift.id).sort((a, b) => a.rekkefølge - b.rekkefølge)}
                isExpanded={expandedHeaders.has(overskrift.id)}
                activeElement={activeElement}
                onToggleHeader={onToggleHeader}
                onEdit={onEdit}
                onDelete={onDelete}
                onOpenHendelseDialog={onOpenHendelseDialog}
                onOpenSangDialog={onOpenSangDialog}
                onSelectSong={onSelectSong}
                calculateStartTime={calculateStartTime}
                formatTime={formatTime}
                formatDurationShort={formatDurationShort}
                readOnly={readOnly}
              />
            </div>
          ))}
        </div>
      </SortableContext>
      
      <DragOverlay dropAnimation={null}>
        {activeElement && (
          <div className={cn(
            "bg-card border-2 border-primary shadow-2xl rounded-lg overflow-hidden",
            activeElement.type === 'overskrift' ? 'min-w-[300px]' : 'min-w-[250px]'
          )}>
            {activeElement.type === 'overskrift' ? (
              <div className="bg-primary text-primary-foreground p-3 flex items-center gap-2">
                <GripVertical className="h-5 w-5" />
                <span className="font-semibold">{activeElement.navn}</span>
                <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground ml-auto">
                  {getHendelserForOverskrift(activeElement.id).length}
                </Badge>
              </div>
            ) : (
              <div className="p-3 flex items-center gap-2">
                <GripVertical className="h-5 w-5 text-muted-foreground" />
                {activeElement.varighet_minutter && (
                  <>
                    <div className="text-xs font-mono text-foreground/80 min-w-[50px]">
                      {calculateStartTime(activeElement.id) ? formatTime(calculateStartTime(activeElement.id)!) : '--:--'}
                    </div>
                    <Badge variant="outline" className="gap-1 bg-primary/10 text-primary border-primary/30 font-mono shrink-0">
                      <Clock className="h-3 w-3" />
                      {formatDurationShort(activeElement.varighet_minutter)}
                    </Badge>
                  </>
                )}
                <span className="font-semibold flex-1">{activeElement.navn}</span>
                {activeElement.type === 'sang' && activeElement.musikk_filer && activeElement.musikk_filer.length > 0 && (
                  <Badge variant="outline" className="gap-1 bg-muted border-muted-foreground/30 text-muted-foreground shrink-0">
                    <FileAudio className="h-3 w-3" />
                    {activeElement.musikk_filer.length}
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

